<template>
  <div class="ai-chat-bubble" :class="[message.role, { error: message.isError }]">
    <div class="bubble-avatar" v-if="message.role === 'assistant'">
      <i class="fa-solid fa-robot"></i>
    </div>

    <div class="bubble-content">
      <div class="bubble-text">
        <span v-html="formattedContent"></span>
        <span v-if="message.isStreaming" class="cursor-blink">|</span>
      </div>

      <div
        v-if="hasSources && !message.isStreaming"
        class="bubble-sources"
      >
        <button
          class="btn-sources"
          @click="showSources = !showSources"
        >
          <i class="fa-solid fa-book-open mr-1"></i>
          Fontes ({{ message.sources.length }})
          <i
            class="fa-solid ml-1"
            :class="showSources ? 'fa-chevron-up' : 'fa-chevron-down'"
          ></i>
        </button>

        <transition name="slide">
          <ul v-if="showSources" class="sources-list">
            <li
              v-for="(source, index) in message.sources"
              :key="index"
              class="source-item"
            >
              <i class="fa-regular fa-file-pdf mr-2"></i>
              <div class="source-info">
                <span class="source-filename">{{ source.filename }}</span>
                <span v-if="source.page_number" class="source-page">
                  pág. {{ source.page_number }}
                </span>
              </div>
              <span class="source-score">
                {{ Math.round(source.score * 100) }}%
              </span>
            </li>
          </ul>
        </transition>
      </div>
    </div>

    <div class="bubble-avatar user-avatar" v-if="message.role === 'user'">
      <span>{{ userInitials }}</span>
    </div>
  </div>
</template>

<script>
export default {
  name: "AIChatBubble",
  props: {
    message: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      showSources: false
    };
  },
  computed: {
    userInitials() {
      if (!this.$user || !this.$user.user) return "?";
      const name = this.$user.user.name || this.$user.user.email || "";
      return name
        .split(" ")
        .map(n => n[0])
        .slice(0, 2)
        .join("")
        .toUpperCase();
    },
    hasSources() {
      return (
        this.message.sources &&
        this.message.sources.length > 0
      );
    },
    formattedContent() {
      if (!this.message.content) return "";
      return this.message.content
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
        .replace(/\n/g, "<br>");
    }
  }
};
</script>

<style lang="scss" scoped>
.ai-chat-bubble {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 16px;
  animation: fadeInUp 0.3s ease;

  &.user {
    flex-direction: row-reverse;

    .bubble-content {
      background: rgba(62, 86, 172, 0.2);
      border-radius: 16px 4px 16px 16px;
      border: 1px solid rgba(62, 86, 172, 0.3);
    }
  }

  &.assistant {
    .bubble-content {
      background: rgba(255, 255, 255, 0.06);
      border-radius: 4px 16px 16px 16px;
      border: 1px solid rgba(255, 255, 255, 0.08);
    }
  }

  &.error .bubble-content {
    background: rgba(220, 53, 69, 0.1);
    border-color: rgba(220, 53, 69, 0.3);
  }
}

.bubble-avatar {
  width: 32px;
  min-width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  background: rgba(171, 133, 229, 0.15);
  color: #ab85e5;
  flex-shrink: 0;

  &.user-avatar {
    background: rgba(62, 86, 172, 0.2);
    color: #5576ec;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.5px;
  }
}

.bubble-content {
  padding: 10px 14px;
  max-width: 85%;
  word-break: break-word;
}

.bubble-text {
  font-size: 13px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.9);

  ::v-deep strong {
    font-weight: 600;
    color: #fff;
  }
}

.cursor-blink {
  animation: blink 0.8s step-end infinite;
  color: #ab85e5;
  font-weight: 300;
}

.bubble-sources {
  margin-top: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  padding-top: 8px;
}

.btn-sources {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  font-size: 11px;
  padding: 4px 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: color 0.2s;

  &:hover {
    color: #ab85e5;
  }
}

.sources-list {
  list-style: none;
  padding: 0;
  margin: 8px 0 0;
}

.source-item {
  display: flex;
  align-items: center;
  padding: 6px 8px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.03);
  margin-bottom: 4px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.6);

  i {
    color: #ab85e5;
    opacity: 0.7;
  }
}

.source-info {
  flex: 1;
  min-width: 0;
}

.source-filename {
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.source-page {
  font-size: 10px;
  opacity: 0.6;
}

.source-score {
  font-size: 10px;
  color: #ab85e5;
  margin-left: 8px;
  white-space: nowrap;
}

.slide-enter-active,
.slide-leave-active {
  transition: all 0.25s ease;
  max-height: 200px;
  overflow: hidden;
}

.slide-enter,
.slide-leave-to {
  max-height: 0;
  opacity: 0;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes blink {
  50% {
    opacity: 0;
  }
}
</style>
